import {
  CLASS_PLACEMENT_KEYS,
  DEFAULT_PLACE_COUNT,
  type ReportAwardsOutput,
  type ReportAwardWinnerOutput,
  type ReportDivisionResultsOutput,
  type ReportPlacementEntryOutput,
  type ReportResultsOutput,
  type TShow,
} from '@schemas'

/** Club awards print only from the requested show's own response, and never once clubs are off. */
export function clubAwardsVisible(
  awards: ReportAwardsOutput | undefined,
  showId: string | undefined,
  currentShow: Pick<TShow, 'enableClubs'> | null | undefined
): boolean {
  return (
    awards?.enableClubs !== false && awards?.showId === showId && currentShow?.enableClubs !== false
  )
}

export function buildHeroAwardWinner(
  awards: ReportAwardsOutput | undefined,
  results: ReportResultsOutput | undefined
): ReportAwardWinnerOutput | null {
  const hero = awards?.heroAward
  if (!hero?.enabled || !hero.winner) return null
  const winner = hero.winner
  for (const division of results?.divisions ?? []) {
    for (const cls of division.classes) {
      if (cls.classCode !== winner.categoryId) continue
      const entry = Object.values(cls.placements)
        .flat()
        .find(entry => entry.exhibitId === winner.entryNumber)
      if (!entry) continue
      return {
        exhibitId: winner.entryNumber,
        entrantId: entry.exhibitorId,
        entrantName: entry.exhibitorName,
        exhibitorId: entry.exhibitorId,
        exhibitorName: entry.exhibitorName,
        entryTitle: entry.entryTitle,
        classId: cls.classId,
        classCode: cls.classCode,
        className: cls.className,
        divisionId: division.divisionId,
        divisionName: division.divisionName,
      }
    }
  }
  return {
    exhibitId: winner.entryNumber,
    entrantId: null,
    entrantName: null,
    exhibitorId: null,
    exhibitorName: null,
    entryTitle: null,
    classId: null,
    classCode: winner.categoryId,
    className: winner.categoryId,
    divisionId: null,
    divisionName: null,
  }
}

const PLACEMENT_SUFFIX_MAP: Record<string, string> = {
  '1st': 'st',
  '2nd': 'nd',
  '3rd': 'rd',
  '4th': 'th',
  '5th': 'th',
  '6th': 'th',
  '7th': 'th',
  '8th': 'th',
  '9th': 'th',
  '10th': 'th',
}

export function getPlacementParts(label: string): { base: string; suffix: string | null } {
  const suffix = PLACEMENT_SUFFIX_MAP[label]
  if (!suffix) {
    return { base: label, suffix: null }
  }
  const base = label.slice(0, label.length - suffix.length)
  return { base, suffix }
}

export type PlacementColumn = {
  label: string
  entries: ReportPlacementEntryOutput[]
}

/**
 * Columns for a class's placements in `order`: every place the class awards, empty or not, then
 * any other place that holds results.
 */
export function orderPlacementColumns(
  placements: ReportDivisionResultsOutput['classes'][number]['placements'],
  {
    order = CLASS_PLACEMENT_KEYS,
    placeCount = DEFAULT_PLACE_COUNT,
  }: { order?: readonly string[]; placeCount?: number } = {}
): PlacementColumn[] {
  const keys = Object.keys(placements)
  const extras = keys
    .filter(place => !order.includes(place))
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))

  const awarded = new Set<string>(CLASS_PLACEMENT_KEYS.slice(0, placeCount))
  return [...order, ...extras]
    .map(place => ({
      label: place,
      entries: placements[place] ?? [],
    }))
    .filter(
      column =>
        (awarded.has(column.label) && order.includes(column.label)) || column.entries.length > 0
    )
}

export function sortDivisions(
  divisions: ReportDivisionResultsOutput[]
): ReportDivisionResultsOutput[] {
  return [...divisions].sort((a, b) => {
    const orderA = a.divisionOrder ?? Number.POSITIVE_INFINITY
    const orderB = b.divisionOrder ?? Number.POSITIVE_INFINITY
    if (orderA !== orderB) {
      return orderA - orderB
    }

    const nameCompare = a.divisionName.localeCompare(b.divisionName)
    if (nameCompare !== 0) {
      return nameCompare
    }

    const codeA = a.divisionCode ?? ''
    const codeB = b.divisionCode ?? ''
    if (codeA !== codeB) {
      return codeA.localeCompare(codeB)
    }

    return a.divisionId.localeCompare(b.divisionId)
  })
}

export function sortClasses(
  classes: ReportDivisionResultsOutput['classes']
): ReportDivisionResultsOutput['classes'] {
  return [...classes].sort((a, b) => {
    const orderA = a.classOrder ?? Number.POSITIVE_INFINITY
    const orderB = b.classOrder ?? Number.POSITIVE_INFINITY
    if (orderA !== orderB) {
      return orderA - orderB
    }

    const nameCompare = a.className.localeCompare(b.className)
    if (nameCompare !== 0) {
      return nameCompare
    }

    const codeA = a.classCode ?? ''
    const codeB = b.classCode ?? ''
    if (codeA !== codeB) {
      return codeA.localeCompare(codeB)
    }

    return a.classId.localeCompare(b.classId)
  })
}

function escapeRegExp(input: string): string {
  return input.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

export function formatDivisionDisplayName(
  name: string,
  divisionCode: string | null | undefined
): string {
  if (!name) return ''

  let formatted = name.trim()
  const code = divisionCode?.trim()

  if (code) {
    const prefixPattern = new RegExp(
      `^${escapeRegExp(code)}\\s*(?:[—–-]|\\u2014|\\u2013|:)\\s*`,
      'i'
    )
    formatted = formatted.replace(prefixPattern, '')
  } else {
    formatted = formatted.replace(/^DIV-[A-Z0-9]+\s*(?:[—–-]|\u2014|\u2013|:)\s*/i, '')
  }

  formatted = formatted.replace(/\s*\(Category\s+[A-Za-z0-9]+\)\s*$/i, '')
  formatted = formatted.replace(/\s{2,}/g, ' ')
  return formatted.trim()
}

const CSV_FORMULA_PREFIX = /^[=+\-@']/

export function sanitizeCsvValue(value: unknown): string {
  if (value === null || value === undefined) return ''
  let stringValue = String(value)
  if (CSV_FORMULA_PREFIX.test(stringValue)) {
    stringValue = `'${stringValue}`
  }

  if (/[",\n\r]/.test(stringValue)) {
    stringValue = `"${stringValue.replace(/"/g, '""')}"`
  }

  return stringValue
}

export function buildResultsCsvRows(results?: ReportResultsOutput): string[][] {
  const header = ['division', 'class', 'placement', 'exhibitId', 'exhibitor', 'entryTitle']
  if (!results || !Array.isArray(results.divisions)) {
    return [header]
  }

  const rows: string[][] = []
  for (const division of sortDivisions(results.divisions)) {
    const divisionLabel = formatDivisionDisplayName(division.divisionName, division.divisionCode)
    for (const showClass of sortClasses(division.classes)) {
      const classLabel = formatClassDisplayName(showClass.className, showClass.classCode)
      const placements = orderPlacementColumns(showClass.placements)
      for (const placement of placements) {
        for (const entry of placement.entries) {
          rows.push([
            divisionLabel,
            classLabel,
            placement.label,
            entry.exhibitId ?? '',
            entry.exhibitorName ?? '',
            entry.entryTitle ?? '',
          ])
        }
      }
    }
  }

  if (rows.length === 0) {
    return [header]
  }

  return [header, ...rows.map(row => row.map(value => sanitizeCsvValue(value)))]
}

export function formatClassDisplayName(name: string, classCode: string | null | undefined): string {
  if (!name) return ''

  let formatted = name.trim()
  const code = classCode?.trim()

  if (code) {
    const prefixPattern = new RegExp(
      `^${escapeRegExp(code)}\\s*(?:[—–-]|\\u2014|\\u2013|:)\\s*`,
      'i'
    )
    formatted = formatted.replace(prefixPattern, '')
  } else {
    formatted = formatted.replace(/^[A-Z]{1,4}\d{0,4}\s*(?:[—–-]|\u2014|\u2013|:)\s*/i, '')
  }

  formatted = formatted.replace(/\s{2,}/g, ' ')
  return formatted.trim()
}

type DivisionWinner = {
  divisionId: string
  divisionName: string
  divisionCode: string | null
  winner: ReportAwardWinnerOutput | null
}

export function renderAwardWinner(winner: ReportAwardWinnerOutput | null | undefined): string {
  if (!winner) {
    return 'Not awarded'
  }

  const exhibitor = winner.exhibitorName?.trim() || 'Unknown exhibitor'
  const title = winner.entryTitle?.trim()
  const classLabel = winner.className
    ? ` • ${winner.classCode ? `${winner.classCode} ` : ''}${winner.className}`
    : ''

  if (!winner.exhibitId) {
    const entrantName = winner.entrantName?.trim() || exhibitor
    return `Entrant ${entrantName}${classLabel}`
  }

  if (title) {
    return `Exhibit ${winner.exhibitId} — ${exhibitor} (“${title}”)${classLabel}`
  }

  return `Exhibit ${winner.exhibitId} — ${exhibitor}${classLabel}`
}

function deriveDivisionFallbackWinner(
  division: ReportDivisionResultsOutput
): ReportAwardWinnerOutput | null {
  const candidates: Array<ReportAwardWinnerOutput & { classOrderRank: number }> = []

  for (const cls of division.classes) {
    const firstPlaceEntries = cls.placements['1st'] ?? []
    if (firstPlaceEntries.length === 0) continue

    const classRank = cls.classOrder ?? Number.MAX_SAFE_INTEGER

    for (const entry of firstPlaceEntries) {
      candidates.push({
        awardId: undefined,
        exhibitId: entry.exhibitId,
        entrantId: entry.exhibitorId ?? null,
        entrantName: entry.exhibitorName ?? null,
        exhibitorId: entry.exhibitorId ?? null,
        exhibitorName: entry.exhibitorName ?? null,
        entryTitle: entry.entryTitle ?? null,
        classId: cls.classId,
        classCode: cls.classCode ?? null,
        className: cls.className,
        divisionId: division.divisionId,
        divisionName: division.divisionName,
        classOrderRank: classRank,
      })
    }
  }

  candidates.sort((a, b) => {
    if (a.classOrderRank !== b.classOrderRank) {
      return a.classOrderRank - b.classOrderRank
    }
    if ((a.className ?? '') !== (b.className ?? '')) {
      return (a.className ?? '').localeCompare(b.className ?? '')
    }
    if ((a.classCode ?? '') !== (b.classCode ?? '')) {
      return (a.classCode ?? '').localeCompare(b.classCode ?? '')
    }
    const exhibitA = a.exhibitId ?? ''
    const exhibitB = b.exhibitId ?? ''
    return exhibitA.localeCompare(exhibitB)
  })

  const winner = candidates[0]
  if (!winner) return null
  const { classOrderRank: _classOrderRank, ...rest } = winner
  void _classOrderRank
  return rest
}

type ReportAwardsWithDivisions = ReportAwardsOutput & {
  bestInDivisionEnabled?: boolean
  bestInDivision?: Array<{
    divisionId: string
    divisionName: string
    divisionCode?: string | null
    winner: ReportAwardWinnerOutput | null | undefined
  }>
}

export function buildDivisionWinners(
  awards: ReportAwardsOutput | undefined,
  results: ReportResultsOutput | undefined
): DivisionWinner[] {
  const typedAwards = awards as ReportAwardsWithDivisions | undefined
  if (typedAwards?.bestInDivisionEnabled === false) {
    return []
  }
  const fromAwards = typedAwards?.bestInDivision
  if (fromAwards && Array.isArray(fromAwards)) {
    return fromAwards.map(item => ({
      divisionId: item.divisionId,
      divisionName: item.divisionName,
      divisionCode: item.divisionCode ?? null,
      winner: item.winner ?? null,
    }))
  }

  if (!results) return []

  return results.divisions.map(division => ({
    divisionId: division.divisionId,
    divisionName: division.divisionName,
    divisionCode: division.divisionCode ?? null,
    winner: deriveDivisionFallbackWinner(division),
  }))
}

export type DivisionWinnerSummary = ReturnType<typeof buildDivisionWinners>
